import React, {useState} from 'react';
import {InteractivePopper} from '@components/call-card/InteractivePopper';
import {TrashIcon} from '@components/icon/TrashIcon';
import {CallCardSource} from '@store/call-card/CallCardSource';
import CallCardActions from '@store/call-card/CallCardActions';
import {UserComment} from 'records/UserComment';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {SetState} from '@store/@types/util/React';
import Button from '@material-ui/core/Button';
import {ShareIcon} from '@components/icon/ShareIcon';
import {createStyles, withStyles} from '@material-ui/core/styles';
import './CallCommentPopper.sass';
import SharingFormActions from 'actions/sharing/SharingFormActions';
import SharingActions from 'actions/sharing/SharingActions';
import AddHighlightToLibraryDialogContainer from '@components/call-card/AddHighlightToLibraryDialogContainer';
import CommentSource from '@store/call-card/CommentSource';

interface CallCommentPopperProps {
  commentRef: HTMLDivElement | null;
  comment: UserComment;
  setEditComment?: SetState<UserComment | undefined>;
  canDelete: boolean;
  callId: FileUploadId;
  open: boolean;
}

export const CallCommentPopper: React.FC<CallCommentPopperProps> = ({
  comment,
  commentRef,
  callId,
  setEditComment,
  open,
  canDelete,
}) => {
  const [showLibraryDialog, setShowLibraryDialog] = useState(false);

  const shareHighlight = () => {
    SharingFormActions.updateSelectedHighlights([{value: comment.id, label: comment.message}]);
    SharingFormActions.updateContentToShare({
      value: 'highlights',
      label: 'Selected highlights',
    });
    SharingActions.showSharingDialog();
  };

  return (
    <>
      <InteractivePopper
        className="call-comment-popper"
        anchorEl={commentRef}
        open={open}
        placement="top-start"
        style={{marginLeft: (commentRef?.clientWidth ?? 0) / 2}}>
        <h4>Comment</h4>
        <p dangerouslySetInnerHTML={{__html: comment.message}}></p>
        <div className="actions">
          {comment.isRange() && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={shareHighlight}
                startIcon={<ShareIcon height="1rem" fill="#FFF" />}>
                Share Highlight
              </Button>
              <Button onClick={() => setShowLibraryDialog(true)} variant="outlined">
                Add to Library
              </Button>
            </>
          )}
          {setEditComment && (
            <Button variant="outlined" onClick={() => setEditComment(comment)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <DeleteButton
              variant="text"
              startIcon={<TrashIcon height="1rem" fill="currentColor" />}
              onClick={async () => {
                if (comment.isRange()) {
                  await CallCardSource.deleteHighlight(callId, comment.id!);
                } else {
                  await CommentSource.deleteComment(callId, comment);
                }
                return CallCardActions.getComments(callId);
              }}>
              Delete
            </DeleteButton>
          )}
        </div>
      </InteractivePopper>
      {showLibraryDialog && (
        <AddHighlightToLibraryDialogContainer
          callId={callId}
          open={showLibraryDialog}
          onClose={() => setShowLibraryDialog(false)}
          selectedHighlight={comment}
        />
      )}
    </>
  );
};

const DeleteButton = withStyles(theme =>
  createStyles({
    root: {
      color: 'var(--error)',
      padding: '5px 15px',
      '&:hover': {
        backgroundColor: 'color-mix(in srgb, var(--error), transparent 90%)',
      },
    },
  })
)(Button);
